import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Mic,
  Send,
  Settings,
  ThumbDown,
  ThumbUp,
  Undo,
} from '@material-ui/icons';
import { useMediaQuery } from '@material-ui/core';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import Typography from 'Components/Atoms/Typography';
import { ReservationContext } from 'Contexts/ReservationContext';
import { AuthContext } from 'Contexts/AuthContext';
import { useHistory, useLocation } from 'react-router';
import { dateHelper } from 'utils/helper';
import ShuffleModeIcon from 'Components/Atoms/Icons/ShuffleModeIcon';
import { Reservation } from 'types/reservations';
import useRealtimeScribe, {
  joinTranscriptParts,
  loadScribeToken,
  SCRIBE_MODEL_ID,
} from 'CustomHooks/useRealtimeScribe';
import useCollection from 'CustomHooks/useCollection';
import {
  TablePlanAssistantChange,
  TablePlanAssistantMessage,
  TablePlanAssistantPendingInteraction,
  TablePlanAssistantPendingInteractionOption,
  TablePlanAssistantSaveResult,
  TablePlanAssistantSaveViolation,
} from 'CustomHooks/useTablePlanAssistant';
import { markdownToInlineHtml } from 'App/Apps/utils/markdown';
import useDocument from 'CustomHooks/useDocument';
import usePinAuth from 'CustomHooks/usePinAuth';
import { trackChange } from 'utils/securityActivity/grant';
import {
  ActivityDetails,
  ActivityOutcome,
  ActivityTarget,
} from 'utils/securityActivity/events';
import {
  TablePlanAssistantQuickActionSettings,
  TablePlanAssistantTemplate,
} from 'types/tablePlanAssistant';
import {
  BUILT_IN_QUICK_ACTIONS,
  getTablePlanAssistantTemplateIcon,
  TablePlanAssistantQuickAction,
} from './quickActions';
import './styles.scss';

type TablePlanAssistantProps = {
  date: string;
  restaurantId: string;
  reservations: Reservation[];
  loadingState?: TablePlanAssistantLoadingState;
};

type TablePlanAssistantLoadingState = {
  reservations?: boolean;
  shifts?: boolean;
  floorPlan?: boolean;
};

const SESSION_QUERY_KEY = 'tablePlanAssistantSessionId';
const EMPTY_HIDDEN_SYSTEM_TEMPLATE_IDS: string[] = [];
const TablePlanAssistantManagementModal = React.lazy(
  () => import('App/Settings/Screens/TablePlanAssistantSettings'),
);

const TablePlanAssistantPortal: React.FC = ({ children }) =>
  createPortal(children, document.body);

const getReservationName = (
  reservations: Reservation[],
  reservationId: string,
  fallback?: string,
) =>
  reservations.find((reservation) => reservation.id === reservationId)?.guest
    ?.name ??
  fallback ??
  reservationId;

const formatChange = (
  change: TablePlanAssistantChange,
  reservations: Reservation[],
) => {
  if (change.type === 'tableChanged') {
    return {
      title: getReservationName(
        reservations,
        change.reservationId,
        change.reservationName,
      ),
      detail: `${change.before.tables.join(', ') || '-'} -> ${
        change.after.tables.join(', ') || '-'
      }`,
    };
  }

  if (change.type === 'block') {
    return {
      title: 'Tische blockieren',
      detail: change.tables.join(', '),
    };
  }

  if (change.type === 'unblock') {
    return {
      title: 'Tische freigeben',
      detail: change.tables.join(', '),
    };
  }

  if (change.type === 'closeSpace') {
    return {
      title: 'Bereich schließen',
      detail: change.space,
    };
  }

  return {
    title: 'Bereich öffnen',
    detail: change.space,
  };
};

/** What an assistant save touches, for the Security Plus activity log. */
const assistantTargets = (
  changes: TablePlanAssistantChange[],
  outcome?: ActivityTarget['outcome'],
): ActivityTarget[] =>
  changes.flatMap((change): ActivityTarget[] => {
    const marked = (target: ActivityTarget) => (outcome ? { ...target, outcome } : target);
    switch (change.type) {
      case 'tableChanged':
        return [marked({ type: 'reservation', id: change.reservationId })];
      case 'block':
      case 'unblock':
        return change.tables.map((id) => marked({ type: 'table', id }));
      default:
        return [marked({ type: 'space', id: change.space })];
    }
  });

const SAVE_OUTCOMES: Record<
  TablePlanAssistantSaveResult,
  (changes: TablePlanAssistantChange[]) => ActivityDetails & { outcome?: ActivityOutcome }
> = {
  saved: (changes) => ({ targets: assistantTargets(changes, 'succeeded') }),
  alreadyApplied: () => ({ outcome: 'cancelled', reason: 'already_applied' }),
  notSaved: () => ({ outcome: 'failed', reason: 'not_saved' }),
  unknown: () => ({ outcome: 'unknown', reason: 'outcome_unknown' }),
};

const normalizeAssistantMessage = (value: string) =>
  value.replace(/\s+/g, ' ').trim();

const getVisibleMessages = (
  messages: TablePlanAssistantMessage[],
  liveMessage: string,
) => {
  const normalizedLiveMessage = normalizeAssistantMessage(liveMessage);
  if (!normalizedLiveMessage) return messages;

  let latestAssistantIndex = -1;
  messages.forEach((message, index) => {
    if (message.role === 'assistant') latestAssistantIndex = index;
  });

  return messages.filter((message, index) => {
    if (index !== latestAssistantIndex || message.role !== 'assistant') {
      return true;
    }

    const normalizedMessage = normalizeAssistantMessage(message.content);

    return (
      normalizedMessage !== normalizedLiveMessage &&
      !normalizedMessage.startsWith(normalizedLiveMessage) &&
      !normalizedLiveMessage.startsWith(normalizedMessage)
    );
  });
};

const getLoadingItems = (loadingState?: TablePlanAssistantLoadingState) =>
  [
    loadingState?.reservations && {
      id: 'reservations',
      label: 'Reservierungen werden geladen',
    },
    (loadingState?.shifts || loadingState?.floorPlan) && {
      id: 'plan',
      label: 'Schichten & Tischplan werden geladen',
    },
  ].filter(Boolean) as { id: string; label: string }[];

const TablePlanAssistantLoadingStatus = ({
  loadingState,
}: {
  loadingState?: TablePlanAssistantLoadingState;
}) => {
  const loadingItems = getLoadingItems(loadingState);

  if (!loadingItems.length) return null;

  return (
    <div className="tablePlanAssistantLoadingStatus" aria-live="polite">
      {loadingItems.map((item) => (
        <span
          key={item.id}
          className={`tablePlanAssistantLoadingItem ${item.id}`}
        >
          <span className="tablePlanAssistantLoadingDot" />
          {item.label}
        </span>
      ))}
    </div>
  );
};

const TablePlanAssistantMessageContent = ({
  message,
}: {
  message: TablePlanAssistantMessage | { role: 'assistant'; content: string };
}) => {
  if (message.role === 'assistant') {
    return (
      <Typography
        variant="text-3"
        color="inherit"
        translation={null}
        className="tablePlanAssistantMessageText"
        dangerouslySetInnerHTML={{
          __html: markdownToInlineHtml(message.content),
        }}
      />
    );
  }

  return (
    <Typography
      variant="text-3"
      color="inherit"
      translation={null}
      className="tablePlanAssistantMessageText"
    >
      {message.content}
    </Typography>
  );
};

const useAssistantQueryParam = () => {
  const history = useHistory();
  const location = useLocation();
  const { TablePlanAssistant } = useContext(ReservationContext);
  const ignoredQuerySessionId = useRef<string | null>(null);

  const querySessionId = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get(SESSION_QUERY_KEY);
  }, [location.search]);

  useEffect(() => {
    if (!querySessionId) {
      ignoredQuerySessionId.current = null;
      return;
    }

    if (querySessionId === ignoredQuerySessionId.current) return;

    if (querySessionId !== TablePlanAssistant.sessionId) {
      TablePlanAssistant.openSession(querySessionId);
    }
  }, [
    querySessionId,
    TablePlanAssistant.openSession,
    TablePlanAssistant.sessionId,
  ]);

  useEffect(() => {
    if (!TablePlanAssistant.sessionId) return;
    if (querySessionId === TablePlanAssistant.sessionId) return;

    const query = new URLSearchParams(location.search);
    query.set(SESSION_QUERY_KEY, TablePlanAssistant.sessionId);

    history.push({
      pathname: location.pathname,
      search: `?${query.toString()}`,
    });
  }, [
    history,
    location.pathname,
    location.search,
    querySessionId,
    TablePlanAssistant.sessionId,
  ]);

  const removeSessionQuery = useCallback(() => {
    const query = new URLSearchParams(location.search);
    ignoredQuerySessionId.current = query.get(SESSION_QUERY_KEY);
    query.delete(SESSION_QUERY_KEY);

    history.replace({
      pathname: location.pathname,
      search: query.toString() ? `?${query.toString()}` : '',
    });
  }, [history, location.pathname, location.search]);

  return { removeSessionQuery };
};

const TablePlanAssistantInput = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const autosize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    const max = 6 * 24;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  }, []);
  const valueRef = useRef(value);
  const scribeBaseValueRef = useRef('');
  const scribeCommittedTextRef = useRef('');
  const [scribeError, setScribeError] = useState<string | null>(null);

  const applyScribeText = useCallback(
    (partialTranscript = '') => {
      onChange(
        joinTranscriptParts(
          scribeBaseValueRef.current,
          scribeCommittedTextRef.current,
          partialTranscript,
        ),
      );
    },
    [onChange],
  );

  const {
    status: scribeStatus,
    isConnected: scribeConnected,
    isTranscribing,
    partialTranscript,
    error: hookScribeError,
    connect: connectScribe,
    disconnect: disconnectScribe,
    clearTranscripts: clearScribeTranscripts,
  } = useRealtimeScribe({
    modelId: SCRIBE_MODEL_ID,
    languageCode: 'de',
    vadSilenceThresholdSecs: 0.8,
    keyterms: [
      'Reservierung',
      'Reservierungen',
      'Tischplan',
      'Terrasse',
      'Schichten',
      'Restaurant',
      'Lounge',
      'Bar',
    ],
    noVerbatim: true,
    onPartialTranscript: ({ text }) => {
      applyScribeText(text);
    },
    onCommittedTranscript: ({ text }) => {
      scribeCommittedTextRef.current = joinTranscriptParts(
        scribeCommittedTextRef.current,
        text,
      );
      applyScribeText();
    },
    onError: (error) => {
      setScribeError(
        error instanceof Error
          ? error.message
          : 'Spracheingabe konnte nicht gestartet werden.',
      );
    },
  });
  const scribeActive =
    scribeStatus === 'connecting' || scribeConnected || isTranscribing;
  const scribeStatusText =
    scribeError ??
    hookScribeError ??
    (scribeActive
      ? isTranscribing || partialTranscript
        ? 'Spracheingabe läuft'
        : 'Mikrofon wird verbunden'
      : null);
  const hasContent = !!value.trim();

  useEffect(() => {
    valueRef.current = value;
    autosize(inputRef.current);
  }, [value, autosize]);

  useEffect(() => {
    if (!autoFocus || disabled) return undefined;

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [autoFocus, disabled]);

  const stopScribe = useCallback(() => {
    disconnectScribe();
  }, [disconnectScribe]);

  useEffect(() => {
    if (!disabled || !scribeActive) return;

    stopScribe();
  }, [disabled, scribeActive, stopScribe]);

  const handleScribeToggle = async () => {
    if (scribeActive) {
      stopScribe();
      return;
    }

    if (disabled) return;

    setScribeError(null);
    scribeBaseValueRef.current = valueRef.current;
    scribeCommittedTextRef.current = '';
    clearScribeTranscripts();

    try {
      const token = await loadScribeToken();
      await connectScribe({ token });
    } catch (error: any) {
      setScribeError(
        error?.message ?? 'Spracheingabe konnte nicht gestartet werden.',
      );
      disconnectScribe();
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (scribeActive) stopScribe();
    onSubmit();
  };

  return (
    <form
      className={`tablePlanAssistantInput${hasContent ? '' : ' empty'}`}
      onSubmit={handleSubmit}
    >
      <textarea
        ref={(el) => {
          inputRef.current = el;
          autosize(el);
        }}
        rows={1}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          autosize(event.currentTarget);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          if (
            event.key === 'Enter' &&
            !event.shiftKey &&
            !event.nativeEvent.isComposing
          ) {
            event.preventDefault();
            if (hasContent && !disabled)
              handleSubmit(event as unknown as FormEvent);
          }
        }}
      />
      {hasContent && (
        <IconButton disabled={disabled} size="small" type="submit">
          <Send />
        </IconButton>
      )}
      <IconButton
        type="button"
        disabled={disabled && !scribeActive}
        size="small"
        className={`tablePlanAssistantScribeButton${
          scribeActive ? ' active' : ''
        }${scribeError || hookScribeError ? ' error' : ''}`}
        title={
          scribeError ||
          hookScribeError ||
          (scribeActive ? 'Spracheingabe stoppen' : 'Spracheingabe starten')
        }
        onClick={handleScribeToggle}
      >
        <Mic />
      </IconButton>
      {!!scribeStatusText && (
        <span className="tablePlanAssistantScribeStatus">
          {scribeStatusText}
        </span>
      )}
    </form>
  );
};

const TablePlanAssistantModal = ({
  open,
  onClose,
  onStart,
  loading,
  loadingState,
  quickActions,
  canManage,
  onManage,
}: {
  open: boolean;
  onClose: () => void;
  onStart: (content: string) => void;
  loading: boolean;
  loadingState?: TablePlanAssistantLoadingState;
  quickActions: TablePlanAssistantQuickAction[];
  canManage: boolean;
  onManage: () => void;
}) => {
  const [content, setContent] = useState('');

  if (!open) return null;

  const submit = (nextContent = content) => {
    if (!nextContent.trim()) return;
    onStart(nextContent.trim());
    setContent('');
  };

  return (
    <TablePlanAssistantPortal>
      <div className="tablePlanAssistantModalBackdrop">
        <div className="tablePlanAssistantPrompt">
          <span className="tablePlanAssistantExperimentalBadge">
            Experimentell - KI kann Fehler machen!
          </span>
          <IconButton
            size="small"
            className="tablePlanAssistantModalClose"
            onClick={onClose}
            title="Assistent schließen"
          >
            <Close />
          </IconButton>
          <div className="tablePlanAssistantPromptContent">
            <TablePlanAssistantLoadingStatus loadingState={loadingState} />
            <TablePlanAssistantInput
              value={content}
              onChange={setContent}
              onSubmit={submit}
              disabled={loading}
              autoFocus
              placeholder="Was soll geändert werden?"
            />
            <div className="tablePlanAssistantQuickActions">
              {quickActions.map((action) => {
                const Icon = action.icon;

                return (
                  <button
                    key={action.id}
                    type="button"
                    disabled={loading}
                    onClick={() => submit(action.text)}
                  >
                    <Icon />
                    <span>{action.label}</span>
                  </button>
                );
              })}
            </div>
            {canManage && (
              <button
                type="button"
                className="tablePlanAssistantManageButton"
                disabled={loading}
                onClick={onManage}
              >
                <Settings fontSize="small" />
                <span>Vorlagen und Routinen anpassen</span>
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          className="tablePlanAssistantBackdropClose"
          aria-label="Assistent schließen"
          onClick={onClose}
        />
      </div>
    </TablePlanAssistantPortal>
  );
};

const TablePlanAssistantDecisionPanel = ({
  interaction,
  disabled,
  onSelect,
}: {
  interaction: TablePlanAssistantPendingInteraction;
  disabled?: boolean;
  onSelect: (option: TablePlanAssistantPendingInteractionOption) => void;
}) => (
  <div
    className={`tablePlanAssistantDecisionPanel ${interaction.type}`}
    aria-live="polite"
  >
    {!!interaction.title && <strong>{interaction.title}</strong>}
    <p>{interaction.question}</p>
    <div className="tablePlanAssistantDecisionOptions">
      {interaction.options.map((option) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(option)}
        >
          <span>{option.label}</span>
          {!!option.description && <small>{option.description}</small>}
        </button>
      ))}
    </div>
  </div>
);

const TablePlanAssistantSavePermissionPanel = ({
  saving,
  violations,
  onAllow,
  onDeny,
}: {
  saving?: boolean;
  violations: TablePlanAssistantSaveViolation[];
  onAllow: () => void;
  onDeny: () => void;
}) => {
  const visibleViolations = violations.slice(0, 3);
  const hiddenViolationCount = violations.length - visibleViolations.length;

  return (
    <div
      className="tablePlanAssistantDecisionPanel permission"
      aria-live="polite"
    >
      <strong>Bestätigung erforderlich</strong>
      <p>
        Diese Änderungen weichen von Tischplan-Regeln ab. Möchtest du sie
        trotzdem speichern?
      </p>
      {!!visibleViolations.length && (
        <ul className="tablePlanAssistantSaveViolations">
          {visibleViolations.map((violation, index) => (
            <li
              key={`${violation.code}-${
                violation.reservationId ?? ''
              }-${index}`}
            >
              {violation.message}
            </li>
          ))}
          {hiddenViolationCount > 0 && (
            <li>{hiddenViolationCount} weitere Hinweise</li>
          )}
        </ul>
      )}
      <div className="tablePlanAssistantDecisionOptions twoActions">
        <button type="button" disabled={saving} onClick={onDeny}>
          <span>Ablehnen</span>
        </button>
        <button type="button" disabled={saving} onClick={onAllow}>
          <span>{saving ? 'Speichert...' : 'Trotzdem speichern'}</span>
        </button>
      </div>
    </div>
  );
};

const TablePlanAssistantSidebar = ({
  onClose,
  reservations,
}: {
  onClose: () => void;
  reservations: Reservation[];
}) => {
  const { TablePlanAssistant } = useContext(ReservationContext);
  const { authenticate } = usePinAuth();
  const [input, setInput] = useState('');
  const [changesOpen, setChangesOpen] = useState(true);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const messages = useMemo(
    () =>
      getVisibleMessages(
        TablePlanAssistant.messages,
        TablePlanAssistant.streaming ? TablePlanAssistant.liveMessage : '',
      ),
    [
      TablePlanAssistant.liveMessage,
      TablePlanAssistant.messages,
      TablePlanAssistant.streaming,
    ],
  );
  const changes = TablePlanAssistant.changes;
  const sessionApplied = !!TablePlanAssistant.session?.applied;
  const sidebarTitle = TablePlanAssistant.session?.title?.trim() || 'Assistent';
  const pendingInteraction =
    !TablePlanAssistant.streaming && !sessionApplied
      ? TablePlanAssistant.pendingInteraction
      : null;

  useEffect(() => {
    const element = messagesRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth',
    });
  }, [
    messages,
    TablePlanAssistant.error,
    TablePlanAssistant.liveMessage,
    TablePlanAssistant.streaming,
  ]);

  const handleSend = () => {
    if (sessionApplied || TablePlanAssistant.saving) return;

    const content = input.trim();
    if (!content) return;

    setInput('');
    TablePlanAssistant.updateSession(content);
  };

  const handleSave = () =>
    authenticate(
      changes.some(
        (change) => change.type === 'block' || change.type === 'unblock',
      )
        ? 'reservations.tableBlocked'
        : 'none',
      async (_, grant) => {
        const result = await trackChange(
          grant,
          'tablePlanAssistant.apply',
          TablePlanAssistant.saveSession(
            TablePlanAssistant.saveRequiresConfirmation,
          ),
          // One save request for all changes. Only a confirmed save marks the
          // targets; an unclear failure may have written part of them.
          {
            targets: assistantTargets(changes),
            fromResult: (result) => SAVE_OUTCOMES[result](changes),
          },
        );

        if (result === 'saved' || result === 'alreadyApplied') onClose();
      },
      undefined,
      { action: 'tablePlanAssistant.apply', targets: assistantTargets(changes) },
    );

  return (
    <TablePlanAssistantPortal>
      <div className="tablePlanAssistantFrame" />
      <aside className="tablePlanAssistantSidebar">
        <div className="tablePlanAssistantSidebarHeader">
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
          <Typography
            variant="text-3"
            weight="bold"
            color="inherit"
            className="tablePlanAssistantSidebarTitle"
          >
            {sidebarTitle}
          </Typography>
          {!sessionApplied && (
            <Button
              variant="primary"
              loading={TablePlanAssistant.saving}
              disabled={
                !changes.length ||
                TablePlanAssistant.streaming ||
                TablePlanAssistant.saving ||
                TablePlanAssistant.saveRequiresConfirmation
              }
              onClick={handleSave}
            >
              Speichern
            </Button>
          )}
        </div>
        <div className="tablePlanAssistantMessages" ref={messagesRef}>
          {messages.map((message) => {
            const showFeedback = message.role === 'assistant' && !!message.id;
            const activeRating = message.feedback?.rating ?? null;
            return (
              <div
                key={message.id ?? `${message.role}-${message.createdAt}`}
                className={`tablePlanAssistantMessage ${message.role}`}
              >
                <TablePlanAssistantMessageContent message={message} />
                {showFeedback && (
                  <div className="tablePlanAssistantMessageFeedback">
                    <IconButton
                      size="small"
                      className={`tablePlanAssistantFeedbackButton${
                        activeRating === 'up' ? ' active' : ''
                      }`}
                      title="Hilfreich"
                      onClick={() =>
                        TablePlanAssistant.rateMessage(
                          message.id!,
                          activeRating === 'up' ? null : 'up',
                        )
                      }
                    >
                      <ThumbUp />
                    </IconButton>
                    <IconButton
                      size="small"
                      className={`tablePlanAssistantFeedbackButton${
                        activeRating === 'down' ? ' active' : ''
                      }`}
                      title="Nicht hilfreich"
                      onClick={() =>
                        TablePlanAssistant.rateMessage(
                          message.id!,
                          activeRating === 'down' ? null : 'down',
                        )
                      }
                    >
                      <ThumbDown />
                    </IconButton>
                  </div>
                )}
              </div>
            );
          })}

          {TablePlanAssistant.streaming && !!TablePlanAssistant.liveMessage && (
            <div className="tablePlanAssistantMessage assistant live">
              <TablePlanAssistantMessageContent
                message={{
                  role: 'assistant',
                  content: TablePlanAssistant.liveMessage,
                }}
              />
            </div>
          )}

          {!!TablePlanAssistant.error && (
            <div
              className={`tablePlanAssistantError${
                TablePlanAssistant.saveRequiresConfirmation ? ' warning' : ''
              }`}
              role={TablePlanAssistant.saveRequiresConfirmation ? 'status' : 'alert'}
            >
              <Typography variant="text-3" color="inherit">
                {TablePlanAssistant.error}
              </Typography>
            </div>
          )}
        </div>

        {!!changes.length && (
          <div className="tablePlanAssistantChanges">
            <div className="tablePlanAssistantChangesHeader">
              <strong>{changes.length} Änderungen</strong>
              <div className="tablePlanAssistantChangesActions">
                <button
                  type="button"
                  className="tablePlanAssistantRevertAll"
                  disabled={
                    TablePlanAssistant.streaming ||
                    TablePlanAssistant.saving ||
                    sessionApplied
                  }
                  onClick={() => TablePlanAssistant.clearChanges()}
                >
                  <Undo />
                  Alle zurücksetzen
                </button>
                <button
                  type="button"
                  className="tablePlanAssistantChangesToggle"
                  onClick={() => setChangesOpen((open) => !open)}
                  aria-label={
                    changesOpen
                      ? 'Änderungsliste schließen'
                      : 'Änderungsliste öffnen'
                  }
                >
                  {changesOpen ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                </button>
              </div>
            </div>

            {changesOpen && (
              <div
                className={`tablePlanAssistantChangesList${
                  changes.length > 3 ? ' scrollable' : ''
                }`}
              >
                {changes.map((change, index) => {
                  const formatted = formatChange(change, reservations);

                  return (
                    <div
                      className="tablePlanAssistantChange"
                      key={`${change.type}-${index}`}
                    >
                      <span>{formatted.title}</span>
                      <strong>{formatted.detail}</strong>
                      <IconButton
                        size="x-small"
                        disabled={
                          TablePlanAssistant.streaming ||
                          TablePlanAssistant.saving ||
                          sessionApplied
                        }
                        onClick={() => TablePlanAssistant.undoChange(index)}
                      >
                        <Undo />
                      </IconButton>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="tablePlanAssistantSidebarInput">
          {TablePlanAssistant.saveRequiresConfirmation ? (
            <TablePlanAssistantSavePermissionPanel
              saving={TablePlanAssistant.saving}
              violations={TablePlanAssistant.saveViolations}
              onAllow={handleSave}
              onDeny={TablePlanAssistant.dismissSaveConfirmation}
            />
          ) : pendingInteraction ? (
            <div className="tablePlanAssistantChoiceComposer">
              <TablePlanAssistantDecisionPanel
                interaction={pendingInteraction}
                disabled={TablePlanAssistant.streaming}
                onSelect={TablePlanAssistant.respondToPendingInteraction}
              />
              <TablePlanAssistantInput
                value={input}
                onChange={setInput}
                onSubmit={handleSend}
                disabled={
                  TablePlanAssistant.streaming ||
                  TablePlanAssistant.saving ||
                  sessionApplied
                }
                placeholder="oder eigene Antwort schreiben …"
                autoFocus
              />
            </div>
          ) : (
            <TablePlanAssistantInput
              value={input}
              onChange={setInput}
              onSubmit={handleSend}
              disabled={
                TablePlanAssistant.streaming ||
                TablePlanAssistant.saving ||
                sessionApplied
              }
              placeholder="Nachfragen"
            />
          )}
        </div>
      </aside>
    </TablePlanAssistantPortal>
  );
};

const TablePlanAssistant = ({
  date,
  restaurantId,
  reservations,
  loadingState,
}: TablePlanAssistantProps) => {
  const { TablePlanAssistant } = useContext(ReservationContext);
  const { roleMap, isGastronautAdmin } = useContext(AuthContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  const isPhone = useMediaQuery('(max-width: 450px)');
  const inPast = date < dateHelper();
  const { removeSessionQuery } = useAssistantQueryParam();
  const waiterOnly =
    roleMap.waiter && !roleMap.admin && !roleMap.chefWaiter && !roleMap.tablet;
  const disabledOnThisDevice = isPhone && waiterOnly;
  const canManage = !!roleMap.admin || !!isGastronautAdmin;
  const [{ data: customTemplates }] = useCollection<TablePlanAssistantTemplate>(
    restaurantId
      ? `restaurants/${restaurantId}/tablePlanAssistantTemplates`
      : '',
    { filter: [['active', '==', true]], limit: 50 },
  );
  const [quickActionSettings] = useDocument<TablePlanAssistantQuickActionSettings>(
    restaurantId
      ? `restaurants/${restaurantId}/tablePlanAssistantSettings`
      : '',
    restaurantId ? 'quickActions' : null,
  );
  const hiddenSystemTemplateIds =
    quickActionSettings.data?.hiddenSystemTemplateIds ??
    EMPTY_HIDDEN_SYSTEM_TEMPLATE_IDS;
  const quickActions = useMemo<TablePlanAssistantQuickAction[]>(
    () => [
      ...BUILT_IN_QUICK_ACTIONS.filter(
        (action) => !hiddenSystemTemplateIds.includes(action.id),
      ),
      ...customTemplates
        .filter(
          (template) =>
            template.active !== false &&
            !!template.name?.trim() &&
            !!template.prompt?.trim(),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((template) => ({
          id: `custom-${template.id}`,
          icon: getTablePlanAssistantTemplateIcon(template.icon),
          label: template.name.trim(),
          text: template.prompt.trim(),
        })),
    ],
    [customTemplates, hiddenSystemTemplateIds],
  );

  const handleManage = useCallback(() => {
    setModalOpen(false);
    setManagementOpen(true);
  }, []);

  const handleStart = useCallback(
    (content: string) => {
      setModalOpen(false);
      TablePlanAssistant.createSession(content);
    },
    [TablePlanAssistant],
  );

  const handleClose = useCallback(() => {
    TablePlanAssistant.close();
    removeSessionQuery();
  }, [TablePlanAssistant, removeSessionQuery]);

  useEffect(() => {
    if (!TablePlanAssistant.active || !TablePlanAssistant.session?.applied) {
      return;
    }

    handleClose();
  }, [
    handleClose,
    TablePlanAssistant.active,
    TablePlanAssistant.session?.applied,
  ]);

  useEffect(() => {
    if (!disabledOnThisDevice || !TablePlanAssistant.active) return;

    handleClose();
  }, [disabledOnThisDevice, handleClose, TablePlanAssistant.active]);

  if (disabledOnThisDevice) {
    return null;
  }

  return (
    <>
      {!TablePlanAssistant.active && (
        <span className="tablePlanAssistantInlineTrigger">
          <IconButton
            disabled={inPast}
            onClick={() => setModalOpen(true)}
            aria-label="Reservierungs-Assistent öffnen"
          >
            <ShuffleModeIcon color={!inPast ? 'secondary' : 'disabled'} />
          </IconButton>
        </span>
      )}

      <TablePlanAssistantModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onStart={handleStart}
        loading={TablePlanAssistant.streaming}
        loadingState={loadingState}
        quickActions={quickActions}
        canManage={canManage}
        onManage={handleManage}
      />

      {managementOpen && (
        <React.Suspense fallback={null}>
          <TablePlanAssistantManagementModal
            onClose={() => setManagementOpen(false)}
          />
        </React.Suspense>
      )}

      {TablePlanAssistant.active && (
        <TablePlanAssistantSidebar
          onClose={handleClose}
          reservations={reservations}
        />
      )}
    </>
  );
};

export default TablePlanAssistant;
